/**
 * Phase 23 / Plan 23-05 / BDSEC-08 — Mollie-Status → AntD-Tag-Color.
 *
 * Quelle: `.claude/skills/sketch-findings-ko-X-main/references/mollie-status-and-actions.md` Z.34-45
 * + `mollie-design-tokens.md` Z.74-87.
 *
 * 7 Mollie-Status → 5 AntD-Tag-Color-Werte:
 *   - open       → "processing" (Blau, mit pulsierendem Dot via AntD)
 *   - pending    → "warning"    (Gelb)
 *   - authorized → "processing" (Blau)
 *   - paid       → "success"    (Grün)
 *   - paid+partial-refund → "warning" (Gelb) — wird vom Caller gemapped
 *   - paid+full-refund    → "orange"  (Orange, Label "Erstattet")
 *   - failed     → "error"      (Rot)
 *   - expired    → "default"    (Grau)
 *   - canceled   → "default"    (Grau, Mollie wire value: 1 l)
 */

export interface StatusTagDescriptor {
    color: string;
    label: string;
}

export type MollieStatusFlowKind = "checkout" | "banktransfer";

/**
 * Erstattungs-Lage einer bezahlten Zahlung.
 *
 * Bewusst ein Union statt des früheren `hasPartialRefund`-Booleans: Eine
 * VOLLSTÄNDIG erstattete Zahlung ist kein Teil-Refund und darf nicht als
 * "Teilweise erstattet" erscheinen (Bug 08/2026 — externe Refunds aus dem
 * Mollie-/PayPal-Dashboard landeten in der Buchungs-Detailseite als
 * "Teilweise erstattet", während die Mollie-Zentrale korrekt "Erstattet"
 * zeigte). Die Zuordnung Betrag → State gehört zum Aufrufer und folgt
 * `deriveBadgeState` (Vergleich gegen `refundBaseAmount`).
 */
export type MollieRefundState = "none" | "partial" | "full";

function getBanktransferStatusTagDescriptor(
    status: string,
    refundState: MollieRefundState,
): StatusTagDescriptor {
    switch (status) {
        case "open":
            return {color: "processing", label: "Wartet auf Überweisung"};
        case "pending":
            return {color: "processing", label: "Zahlung in Bearbeitung"};
        // WS-4: SEPA-Überweisung hat keine authorized/Reservierungs-Phase. Ein
        // defensiv möglicher "authorized"-Status fällt daher in den neutralen
        // default ("Status unbekannt") statt fälschlich "Reserviert" anzuzeigen.
        case "paid":
            if (refundState === "full") {
                return {color: "orange", label: "Erstattet"};
            }
            if (refundState === "partial") {
                return {color: "warning", label: "Teilweise erstattet"};
            }
            return {color: "success", label: "Zahlung eingegangen"};
        case "failed":
        case "canceled":
            return {color: "default", label: "Zahlungsversuch nicht aktiv"};
        case "expired":
            return {color: "default", label: "Abgelaufen"};
        case "superseded":
            // Von einem neueren Versuch (z. B. durch die Korrektur) abgelöst.
            return {color: "default", label: "Ersetzt"};
        default:
            return {color: "default", label: "Status unbekannt"};
    }
}

/**
 * Liefert AntD-Tag-Color + Default-Label für einen Mollie-Status.
 *
 * @param status       Mollie wire status.
 * @param refundState  Nur für status === "paid": "partial" → warning-Variante,
 *                     "full" → "Erstattet" (orange, wie in der Mollie-Zentrale).
 */
export function getMollieStatusTagDescriptor(
    status: string,
    refundState: MollieRefundState = "none",
    flowKind: MollieStatusFlowKind = "checkout",
): StatusTagDescriptor {
    if (flowKind === "banktransfer") {
        return getBanktransferStatusTagDescriptor(status, refundState);
    }

    switch (status) {
        case "open":
            // B-188: NICHT "Link versendet" — der Status belegt nur einen offenen
            // Checkout, keinen Mail-Versand (EUA-Buchungen liefen lange ganz ohne Mail).
            return {color: "processing", label: "Zahlung ausstehend"};
        case "pending":
            return {color: "warning", label: "In Bearbeitung"};
        case "authorized":
            return {color: "processing", label: "Reserviert"};
        case "paid":
            if (refundState === "full") {
                // Gleiches Label wie der Badge der Mollie-Zentrale
                // (BadgeState.Refunded → "Erstattet"), damit Liste und Detail
                // dieselbe Zahlung nicht unterschiedlich benennen.
                return {color: "orange", label: "Erstattet"};
            }
            if (refundState === "partial") {
                return {color: "warning", label: "Teilweise erstattet"};
            }
            return {color: "success", label: "Bezahlt"};
        case "failed":
            return {color: "error", label: "Fehlgeschlagen"};
        case "expired":
            return {color: "default", label: "Abgelaufen"};
        case "canceled":
            return {color: "default", label: "Storniert"};
        case "superseded":
            // Von einem neueren Versuch (z. B. durch die Korrektur) abgelöst.
            return {color: "default", label: "Ersetzt"};
        default:
            // OOS-3 (UAT 2026-05-17): Vorher wurde der raw status-string zurückgegeben
            // (z.B. "unknown" oder ""), was im UI als `UNKNOWN`-Tag erschien und für
            // Admins verwirrend war. Jetzt ein verständlicher deutscher Fallback-Label.
            // Backend kann beim allerersten MolliePaymentAttempt vor dem ersten Mollie-
            // Webhook einen leeren/unknown Status liefern.
            return {color: "default", label: "Status unbekannt"};
    }
}
